import { axios } from './axiosClient';
import { validate } from '@/utils/errors/helpers';
import { getReportingTemplatesByConditionSchema } from '@/types/schemas/reporting/getReportingTemplatesByCondition';
import type { TableFilterData } from '@/types/Base';
import type { ReportingTemplate } from '@/types/Reporting';
import { getReportingTemplateByIdSchema } from '@/types/schemas/reporting/getReportingTemplateById';

export async function getReportingTemplatesByCondition({
  page = 1,
  itemsPerPage = 0,
  query = '',
  sortColumn,
  sortAscending,
  functionalityEnums,
  isManager,
  isClient,
  isActive,
}: TableFilterData & {
  functionalityEnums?: number[];
  isManager?: boolean;
  isClient?: boolean;
}): Promise<{
  data: ReportingTemplate[];
  count: number;
}> {
  const skip = (page - 1) * itemsPerPage;
  const response = await axios.post(
    `/v2/reporting/templates/search?take=${itemsPerPage}&skip=${skip}`,
    {
      searchText: query,
      sortColumn,
      sortAscending,
      functionalityEnums,
      isClient,
      isManager,
      isActive,
    }
  );

  const valid = validate(getReportingTemplatesByConditionSchema, response.data.data);
  return { data: valid, count: response.data.count };
}

export async function getReportingSchema(sql: string, parameters: Record<string, unknown> = {}) {
  const payload = { sql, parameters };
  const response = await axios.post('/v2/reporting/templates/schema', payload);
  return response.data;
}

export async function removeReportingTemplatesByIds(ids: string[]): Promise<string | null> {
  const response = await axios.post('/v2/reporting/templates/delete-by-ids', ids, {
    validateStatus: (status: number) => status === 400 || status == 204,
  });
  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function addOrEditReportingTemplate(item: ReportingTemplate) {
  const response = await axios.post('/v2/reporting/templates', item);
  if (response.status === 201) return true;
  return false;
}

export async function getReportingTemplateById(id: string) {
  const response = await axios.get(`/v2/reporting/templates/${id}`, {
    validateStatus: (status: number) => status == 404 || status == 200,
  });
  if (response.status === 404) return null;
  return validate(getReportingTemplateByIdSchema, response.data);
}
